import React, { useEffect } from "react";
import { Modal, Form, Select, InputNumber, Input, Button } from "antd";
import { HistoryOutlined } from "@ant-design/icons";

const MaterialFormModal = ({
  open,
  onCancel,
  onSubmit,
  isEditing,
  selectedItem,
  categoryOptions,
  subcategoryOptions,
  unitsOptions,
  locationsOptions,
  onHistoryClick,
}) => {
  const [form] = Form.useForm();

  useEffect(() => {
    if (selectedItem && open) {
      form.setFieldsValue(selectedItem);
    } else {
      form.resetFields();
    }
  }, [selectedItem, open]);

  const handleCreate = () => {
    form
      .validateFields()
      .then((values) => {
        const formattedValues = {
          materialName: values.materialName || "",
          sku: values.sku || "",
          itemVendor: values.itemVendor || "",
          category: values.category || "",
          subcategory: values.subcategory || "",
          description: values.description || "",
          note: values.note || "",
          unitPrice: values.unitPrice ?? 0,
          unit: values.unit || "",
          currentStock: values.currentStock ?? 0,
          minStock: values.minStock ?? 0,
          maxStock: values.maxStock ?? 0,
          itemBay: values.itemBay || "",
          itemShelf: values.itemShelf || "",
          itemRow: values.itemRow || "",
          storageLocation: values.storageLocation || "",
        };
        onSubmit(formattedValues);
        form.resetFields();
      })
      .catch((err) => console.log("Validation failed:", err));
  };

  return (
    <Modal
      title={isEditing ? "Edit Material" : "Add New Material"}
      open={open}
      centered
      onCancel={onCancel}
      footer={[
        isEditing && (
          <Button
            key="history"
            icon={<HistoryOutlined />}
            style={{ float: "left" }}
            onClick={() => onHistoryClick && onHistoryClick()}
          >
            Change History
          </Button>
        ),
        <Button key="cancel" onClick={onCancel}>
          Cancel
        </Button>,
        <Button key="create" type="primary" onClick={handleCreate}>
          {isEditing ? "Update" : "Create"}
        </Button>,
      ].filter(Boolean)}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          label="Material Name"
          name="materialName"
          rules={[{ required: true, message: "Please enter material name" }]}
        >
          <Input placeholder="e.g., Steel Rebar #4" />
        </Form.Item>

        <Form.Item label="SKU" name="sku">
          <Input placeholder="e.g., RB-20-4-60" />
        </Form.Item>

        <Form.Item label="Vendor" name="itemVendor">
          <Input placeholder="e.g., ABC Supply" />
        </Form.Item>

        <Form.Item label="Category" name="category">
          <Select placeholder="Select a category">
            {categoryOptions?.map((cat) => (
              <Select.Option key={cat.categoryID} value={cat.name}>
                {cat.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item label="Subcategory" name="subcategory">
          <Select placeholder="Select a subcategory">
            {subcategoryOptions?.map((cat) => (
              <Select.Option key={cat.subcategoryID} value={cat.name}>
                {cat.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item label="Description" name="description">
          <Input.TextArea rows={3} placeholder="Material description..." />
        </Form.Item>

        <Form.Item
          label="Note"
          name="note"
          dependencies={["currentStock"]}
          rules={[
            ({ getFieldValue }) => ({
              validator(_, value) {
                const currentStock = Number(getFieldValue("currentStock"));
                const previousStock = Number(selectedItem?.currentStock);
                const isStockDecrease =
                  isEditing &&
                  Number.isFinite(currentStock) &&
                  Number.isFinite(previousStock) &&
                  currentStock < previousStock;

                if (!isStockDecrease || String(value || "").trim()) {
                  return Promise.resolve();
                }

                return Promise.reject(
                  new Error("A note is required when decreasing inventory"),
                );
              },
            }),
          ]}
        >
          <Input.TextArea
            rows={3}
            placeholder="Add a note. Required when decreasing inventory."
          />
        </Form.Item>

        <div className="d-flex gap-3">
          <Form.Item
            label="Unit Price ($)"
            name="unitPrice"
            rules={[{ required: true, message: "Enter unit price" }]}
          >
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item
            label="Unit"
            name="unit"
            rules={[{ required: true, message: "Select unit" }]}
            className="flex-grow-1"
          >
            <Select placeholder="Select unit">
              {unitsOptions?.map((unit) => (
                <Select.Option key={unit.unitID} value={unit.name}>
                  {unit.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </div>

        <div className="d-flex gap-3">
          <Form.Item
            label="Current Stock"
            name="currentStock"
            rules={[{ required: true, message: "Enter current stock" }]}
          >
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item
            label="Minimum Stock"
            name="minStock"
            rules={[{ required: true, message: "Enter minimum stock" }]}
          >
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item
            label="Maximum Stock"
            name="maxStock"
            rules={[{ required: true, message: "Enter maximum stock" }]}
          >
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>
        </div>

        <div className="d-flex gap-3">
          <Form.Item label="Bay" name="itemBay">
            <Input placeholder="e.g., A1" />
          </Form.Item>

          <Form.Item label="Row" name="itemRow">
            <Input placeholder="e.g., B" />
          </Form.Item>

          <Form.Item label="Shelf" name="itemShelf">
            <Input placeholder="e.g., 1" />
          </Form.Item>
        </div>

        <Form.Item
          label="Storage Location"
          name="storageLocation"
          rules={[{ required: true, message: "Select location" }]}
        >
          <Select placeholder="Select storage location">
            {locationsOptions?.map((loc) => (
              <Select.Option key={loc.locationID} value={loc.name}>
                {loc.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default MaterialFormModal;
